import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace LegalObligationLibrary {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface LegalObligationHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface LegalObligationEntity {
        componentType: string;
        policyCat: string;
        policyCriticality: string;
        policyDesc: string;
        policyHistory: LegalObligationHistoryItem[];
        policyID: string;
        policyIDstatus: "active" | "inactive" | "deleted";
        policyLocation: string;
        policyOwner: string;
        policyPublishInPrivacyLegalObligation: "Yes" | "No";
        policyRisk: string;
        policySubprocessor: "Yes" | "No";
        policyTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface LegalObligationLibraryResponse {
        statusCode?: number;
        message?: string;
        policies?: LegalObligationEntity[];
        entry?: LegalObligationEntity;
        error?: string | null;
    }

    export class LegalObligationInstance {
        private data: LegalObligationLibrary.LegalObligationEntity;

        constructor(data: LegalObligationLibrary.LegalObligationEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): LegalObligationLibrary.LegalObligationEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the LegalObligation class
        public async edit(updates: Partial<LegalObligationLibrary.LegalObligationEntity>): Promise<LegalObligationLibrary.LegalObligationLibraryResponse | void> {
            return LegalObligationLibrary.LegalObligationItem.Edit(this.data.policyID, updates);
        }
    }

    export class LegalObligationItem {

        /**
         * Add an policy to the API.
         * @param id - The ID of the policy to add.
         * @param policyData - Partial policy data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            policyData: Partial<LegalObligationEntity>,
            callback?: (res: LegalObligationLibraryResponse) => any
        ): Promise<LegalObligationLibraryResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/legalObligations/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Delete an policy from the API.
         * @param id - The ID of the policy to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: LegalObligationLibraryResponse) => any
        ): Promise<LegalObligationLibraryResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/legalObligations/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing policy in the API.
         * @param id - The ID of the policy to edit.
         * @param policyData - Partial policy data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            policyData: Partial<LegalObligationEntity>,
            callback?: (res: LegalObligationLibraryResponse) => any
        ): Promise<LegalObligationLibraryResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...policyData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/legalObligations/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...policyData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all policies from the API.
         */
        static async Get(): Promise<LegalObligationLibrary.LegalObligationEntity[] | undefined>;

        /**
         * Fetch a specific policy by ID from the API.
         * @param id - The ID of the policy to fetch.
         */
        static async Get(id: string | number): Promise<LegalObligationInstance | undefined>;

        static async Get(id?: string | number): Promise<LegalObligationLibrary.LegalObligationEntity[] | LegalObligationInstance | undefined> {
            if (typeof id === 'undefined' || id === 'all') {
                // Fetch all policies
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/legalObligations/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.data;
            } else {
                // Fetch single policy
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/legalObligations/get/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                if (res.data) {
                    return new LegalObligationInstance(res.data);
                } else {
                    return undefined;
                }
            }
        }
    }

    export const LegalObligation = new Proxy(LegalObligationItem, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            }

            return target[lowerCasedProp];
        }
    });
}